import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, LogIn, ShieldCheck } from "lucide-react";
import toast from "react-hot-toast";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { customerLoginScopes } from "@/auth/msalConfig";
import { authApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const { instance, inProgress } = useMsal();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const prefillEmail = searchParams.get("email") ?? undefined;

  // Clear any stale "interaction in progress" state on mount — happens if a
  // previous popup was closed without completing the flow.
  useEffect(() => {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("msal.") && k.includes("interaction.status"))
      .forEach((k) => sessionStorage.removeItem(k));

    instance.handleRedirectPromise().catch(() => {
      /* no-op — there was nothing to redirect from */
    });
  }, [instance]);

  const signIn = async () => {
    if (inProgress !== InteractionStatus.None) {
      toast("An sign-in attempt is already underway");
      return;
    }
    setLoading(true);
    try {
      const result = await instance.loginPopup({
        scopes: customerLoginScopes,
        prompt: "select_account",   // always show the picker — fixes "stuck on previous account"
        ...(prefillEmail ? { loginHint: prefillEmail } : {}),
      });
      const token = result.idToken ?? result.accessToken;
      const data = await authApi.exchange(token, "customer");
      setAuth(data.access_token, data.refresh_token, data.user);
      toast.success(`Welcome back, ${data.user.first_name || data.user.email}`);
      navigate("/dashboard", { replace: true });
    } catch (err: unknown) {
      console.error(err);
      const message =
        err instanceof Error ? err.message : "Sign-in failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-slate-100 px-4">
      <div className="w-full max-w-md card p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-brand-700 flex items-center justify-center">
            <Building2 className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">UmdoniWay</h1>
            <p className="text-sm text-slate-500">Utility monitoring for SA properties</p>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome back</h2>
        <p className="text-sm text-slate-600 mb-6">
          Sign in to monitor electricity, water and gas consumption across your portfolio.
        </p>
        <button
          onClick={signIn}
          disabled={loading}
          className="btn-primary w-full py-3"
          type="button"
        >
          <LogIn size={18} />
          {loading ? "Signing in…" : "Sign in with Email or Google"}
        </button>
        <div className="mt-6 pt-6 border-t border-slate-100">
          <a
            href="/staff/login"
            className="flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-brand-700"
          >
            <ShieldCheck size={16} />
            UmdoniWay staff sign-in
          </a>
        </div>
      </div>
    </div>
  );
}
